/**
 * Coinstore timeframe configuration and interval mapping.
 * REST API format: '1min', '5min', '60min', '4hour', etc.
 * WebSocket format: 'min_1', 'min_5', 'hour_1', etc. (different!)
 * NOTE: Month timeframe is NOT supported by Coinstore.
 * @see https://coinstore-openapi.github.io/en/#get-k-line
 * @see https://coinstore-openapi.github.io/en/#k-line-streams
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * Coinstore supported kline intervals.
 */
export const COINSTORE_TIMEFRAMES: TimeframeConfig = {
  native: ['1min', '5min', '15min', '30min', '60min', '4hour', '12hour', '1day', '1week'],
  synthetic: ['1s'], // Use ticker for 1s
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('coinstore', COINSTORE_TIMEFRAMES);

/**
 * Maps TradingView resolution to Coinstore REST API interval format.
 */
export const mapToCoinstoreInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  // Exact matches for all supported intervals
  if (seconds === 60) return '1min';
  if (seconds === 300) return '5min';
  if (seconds === 900) return '15min';
  if (seconds === 1800) return '30min';
  if (seconds === 3600) return '60min';
  if (seconds === 14400) return '4hour';
  if (seconds === 43200) return '12hour';
  if (seconds === 86400) return '1day';
  if (seconds === 604800) return '1week';

  // Default to closest supported interval (round down)
  if (seconds < 60) return '1min';
  if (seconds < 300) return '1min';
  if (seconds < 900) return '5min';
  if (seconds < 1800) return '15min';
  if (seconds < 3600) return '30min';
  if (seconds < 14400) return '60min';
  if (seconds < 43200) return '4hour';
  if (seconds < 86400) return '12hour';
  if (seconds < 604800) return '1day';

  return '1week';
};

/**
 * Maps TradingView resolution to Coinstore WebSocket interval format.
 * NOTE: REST uses different format (1min, 5min, etc.).
 */
export const mapToCoinstoreWsInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  // Exact matches for all supported intervals
  if (seconds === 60) return 'min_1';
  if (seconds === 300) return 'min_5';
  if (seconds === 900) return 'min_15';
  if (seconds === 1800) return 'min_30';
  if (seconds === 3600) return 'hour_1';
  if (seconds === 14400) return 'hour_4';
  if (seconds === 43200) return 'hour_12';
  if (seconds === 86400) return 'day_1';
  if (seconds === 604800) return 'week_1';

  // Default to closest supported interval (round down)
  if (seconds < 60) return 'min_1';
  if (seconds < 300) return 'min_1';
  if (seconds < 900) return 'min_5';
  if (seconds < 1800) return 'min_15';
  if (seconds < 3600) return 'min_30';
  if (seconds < 14400) return 'hour_1';
  if (seconds < 43200) return 'hour_4';
  if (seconds < 86400) return 'hour_12';
  if (seconds < 604800) return 'day_1';

  return 'week_1';
};
